import React, { useEffect, useRef, useState } from 'react';
import { View, Modal, ActivityIndicator, TouchableOpacity, Animated } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { clonedPlateService } from '../../../services/central-api/cloned-plate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownLeftAndUpRightToCenter, faUpRightAndDownLeftFromCenter } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../../services/translate';
import CustomButton from '../../../components/CustomButton';
import Toast from 'react-native-toast-message';
import RouteMap from '../../../components/RouteMap';
import CloneDetectionModal from './CloneDetectionModal';

interface Props {
    setModalVisible: (value: boolean) => void;
    isModalVisible: boolean;
    cloneEventId: number | undefined;
    onClose: () => void;
}

export default function CloneSuspicionModal({ isModalVisible, setModalVisible, cloneEventId, onClose }: Props) {
    const { styles, theme } = useStyles(styleSheet);

    const [zoom, setZoom] = useState<number>(11);
    const [isMapMaximized, setIsMapMaximized] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [cloneEvent, setCloneEvent] = useState<CloneEvent>();

    const mapWidth = useRef(new Animated.Value(225)).current;
    const mapHeight = useRef(new Animated.Value(225)).current;

    async function getCloneEvent() {
        if (!cloneEventId) {
            return;
        }
        try {
            setIsLoading(true);
            const response = await clonedPlateService.getCloneEventById(cloneEventId);
            setCloneEvent(response);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    async function cloneEventReviewed(isValidClone: boolean) {
        if (!cloneEventId) {
            return;
        }
        try {
            await clonedPlateService.cloneEventReviewed(cloneEventId, isValidClone);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setModalVisible(false);
            onClose();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function onMapAnimationEnd() {
        if (isMapMaximized) {
            setZoom(10);
        } else {
            setZoom(11);
        }
    }

    useEffect(() => {
        const targetWidth = isMapMaximized ? 640 : 225;
        const targetHeight = isMapMaximized ? 800 : 225;

        Animated.parallel([
            Animated.timing(mapWidth, {
                toValue: targetWidth,
                duration: 300,
                useNativeDriver: false,
            }),
            Animated.timing(mapHeight, {
                toValue: targetHeight,
                duration: 300,
                useNativeDriver: false,
            }),
        ]).start(onMapAnimationEnd);
    }, [isMapMaximized]);

    useEffect(() => {
        if (cloneEventId) {
            getCloneEvent();
        }
    }, [cloneEventId]);

    if (!cloneEvent) {
        return <></>;
    }

    function getPoints() {
        const points: CameraMap[] = [];
        if (cloneEvent?.firstDetection.camera) {
            points.push({
                id: cloneEvent.firstDetection.id.toString(),
                hasLpr: false,
                type: 'lpr',
                latitude: cloneEvent.firstDetection.camera.latitude,
                longitude: cloneEvent.firstDetection.camera.longitude,
                originalCameraId: null,
            });
        }
        if (cloneEvent?.secondDetection.camera) {
            points.push({
                id: cloneEvent.secondDetection.id.toString(),
                hasLpr: false,
                type: 'lpr',
                latitude: cloneEvent.secondDetection.camera.latitude,
                longitude: cloneEvent.secondDetection.camera.longitude,
                originalCameraId: null,
            });
        }
        return points;
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                setModalVisible(false);
                onClose();
            }}
        >
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    {
                        isLoading ?
                            <View style={styles.loadingContainer}>
                                <ActivityIndicator size='large' color={theme.colors.iconColor} />
                            </View> :
                            <View style={styles.wholeContentContainer}>
                                <View style={styles.infoContainer}>
                                    <View style={styles.detectionContainer}>
                                        <CloneDetectionModal detection={cloneEvent.firstDetection} event={cloneEvent} />
                                        <CloneDetectionModal detection={cloneEvent.secondDetection} event={cloneEvent} />
                                    </View>
                                    {!cloneEvent.reviewed ?
                                        <CustomButton
                                            style={styles.btnStyle}
                                            text={translate('confirmClone')}
                                            onPress={() => cloneEventReviewed(true)}
                                        /> : null}
                                    {!cloneEvent.reviewed ?
                                        <CustomButton
                                            style={styles.btnStyle}
                                            type='secondary'
                                            text={translate('ignoreDuplication')}
                                            onPress={() => cloneEventReviewed(false)}
                                        /> : null}
                                </View>
                                <Animated.View style={[styles.mapContainer, { width: mapWidth, height: mapHeight }]}>
                                    <RouteMap cameraPositions={getPoints()} newZoom={zoom} zoomControl controlSize={25} />

                                    <TouchableOpacity style={styles.mapButton} onPress={() => setIsMapMaximized(!isMapMaximized)}>
                                        {isMapMaximized
                                            ? <FontAwesomeIcon size='xl' color={theme.colors.primaryButton.background} rotation={90} icon={faDownLeftAndUpRightToCenter} />
                                            : <FontAwesomeIcon size='xl' color={theme.colors.primaryButton.background} rotation={90} icon={faUpRightAndDownLeftFromCenter} />}
                                    </TouchableOpacity>
                                </Animated.View>
                            </View>
                    }
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet(theme => ({
    wholeContentContainer: {
        position: 'relative',
        flexDirection: 'row',
        width: '100%',
        maxWidth: 820,
        justifyContent: 'center'
    },
    infoContainer: {
        flexDirection: 'column',
        gap: 20,
        justifyContent: 'center',
        alignSelf: 'center'
    },
    detectionContainer: {
        flexDirection: 'row',
        gap: 80,
        height: '100%',
        width: '100%',
        justifyContent: 'center',
        maxHeight: 700
    },
    loadingContainer: {
        flexDirection: 'row',
        height: '80%',
        width: '100%',
        justifyContent: 'center',
        maxHeight: 700
    },
    btnStyle: {
        width: 820,
        alignSelf: 'center'
    },
    mapContainer: {
        position: 'absolute',
        right: -237, // initial size (225px) + 12 (gap)
        bottom: 0,
        borderRadius: 8,
        overflow: 'hidden',
        shadowColor: 'black',
        shadowOffset: {
            height: 5,
            width: -4
        },
        shadowOpacity: 0.4,
        shadowRadius: 12,
    },
    mapButton: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: 45,
        height: 45,
        justifyContent: 'center',
        backgroundColor: theme.colors.primaryButton.text,
        borderBottomRightRadius: 8
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16
    },
}));
